import type { ReactNode } from "react";
import { Popover, Flex, Text } from "@radix-ui/themes";
import type { PageKey, AppNavigationTarget } from "../../appNavTypes";
import { GAME_NAV_TABS, battleTabFor, type GameNavTab } from "./gameNavConfig";
import { GameProfileCard } from "./GameProfileCard";
import { GameTrainerMenu } from "./GameTrainerMenu";
import { GameMobileBar } from "./GameMobileBar";
import { useAttentionPets } from "../../hooks/useAttentionPets";
import { useDailyRivalAvailable } from "../../hooks/useDailyRival";
import { useWalletConnection } from "../../auth/useWalletConnection";
import { useT } from "../../i18n/i18nContext";

interface GameNavProps {
  activePage: PageKey;
  onNavigate: (target: AppNavigationTarget) => void;
  /** Acting identity / guest sentinel — drives the profile card and the notification dot. */
  actingAddress: string | null;
  /** Login / account control (rail foot on desktop, trailing slot on mobile). */
  accountSlot?: ReactNode;
}

/**
 * Primary (and only) app navigation. Desktop: a left side-rail — logo (→ home) at the top,
 * primary entries (Play / Campaign / Battle) as large stacked tabs, then secondary entries
 * (Adopt / Collection / Rankings) as compact icon-left rows, then the player profile +
 * account at the foot. Mobile: a compact bottom tab bar. Layout swap is CSS-driven.
 *
 * The Play entry shows a red notification dot when a Suikamon needs care. The Battle entry
 * opens a small popup to choose Training / Solo / Squad instead of navigating directly —
 * except for a visitor with no identity, who is taken straight into Training (battleTabFor).
 */
export function GameNav({ activePage, onNavigate, actingAddress, accountSlot }: GameNavProps) {
  const t = useT();
  const { count: attentionCount } = useAttentionPets(actingAddress);
  const dailyRivalAvailable = useDailyRivalAvailable(actingAddress);
  // Ranked needs an authenticated identity; a guest or a first-time visitor is sent straight
  // into Training instead of a sign-up gate. See battleTabFor.
  const { isConnected } = useWalletConnection();

  const hasDot = (notify: GameNavTab["notify"]): boolean =>
    (notify === "pet-care" && attentionCount > 0) ||
    (notify === "daily-rival" && dailyRivalAvailable);

  const handleActivate = (path: string, externalUrl?: string) => {
    if (externalUrl) {
      window.open(externalUrl, "_blank", "noopener,noreferrer");
      return;
    }
    onNavigate(path as AppNavigationTarget);
  };

  const tabClassName = (tab: GameNavTab, isActive: boolean): string =>
    `game-nav__tab${isActive ? " game-nav__tab--active" : ""}` +
    `${tab.mobile ? "" : " game-nav__tab--desktop-only"}` +
    `${tab.secondary ? " game-nav__tab--secondary" : ""}`;

  const tabInner = (tab: GameNavTab, showDot: boolean) => (
    <>
      <span className="game-nav__icon" aria-hidden="true">
        <tab.Icon size={22} strokeWidth={2} />
        {showDot && <span className="game-nav__dot" />}
      </span>
      <span className="game-nav__label">{t(tab.label)}</span>
    </>
  );

  return (
    <>
    <nav className="game-nav" aria-label={t("navigation.game.ariaLabel")}>
      <button
        type="button"
        className="game-nav__logo"
        onClick={() => onNavigate("/")}
        aria-label={t("navigation.game.homeLogo")}
      >
        <img src="/xlogo.png" alt={t("common.brand.logoAlt")} className="game-nav__logo-img" />
      </button>

      <div className="game-nav__items">
        {GAME_NAV_TABS.filter((entry) => !entry.inTrainerMenu && !entry.hiddenFromMenu).map((entry) => {
          const tab = battleTabFor(entry, isConnected);
          const isActive = tab.activePages.includes(activePage);
          const showDot = hasDot(tab.notify);

          // Battle (or any entry with a submenu): open a small popup of sub-options.
          if (tab.submenu) {
            return (
              <Popover.Root key={tab.key}>
                <Popover.Trigger>
                  <button
                    type="button"
                    className={tabClassName(tab, isActive)}
                    aria-label={t(tab.ariaLabel)}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {tabInner(tab, showDot)}
                  </button>
                </Popover.Trigger>
                <Popover.Content side="right" align="center" size="1" className="game-popover" style={{ maxWidth: 240 }}>
                  <Flex direction="column" gap="1">
                    {tab.submenu.map((sub) => (
                      <Popover.Close key={sub.key}>
                        <button
                          type="button"
                          className="game-submenu__item"
                          onClick={() => onNavigate(sub.path as AppNavigationTarget)}
                        >
                          <span className="game-submenu__icon" aria-hidden="true">
                            <sub.Icon size={18} strokeWidth={2} />
                          </span>
                          <span className="game-submenu__meta">
                            <Text size="2" weight="bold">{t(sub.label)}</Text>
                            <Text size="1" className="game-submenu__desc">{t(sub.description)}</Text>
                          </span>
                        </button>
                      </Popover.Close>
                    ))}
                  </Flex>
                </Popover.Content>
              </Popover.Root>
            );
          }

          return (
            <button
              key={tab.key}
              type="button"
              className={tabClassName(tab, isActive)}
              onClick={() => handleActivate(tab.path, tab.externalUrl)}
              aria-label={showDot ? t("navigation.game.newNotification", { label: t(tab.ariaLabel) }) : t(tab.ariaLabel)}
              aria-current={isActive ? "page" : undefined}
            >
              {tabInner(tab, showDot)}
            </button>
          );
        })}
      </div>

      <div className="game-nav__profile">
        <GameProfileCard actingAddress={actingAddress} onNavigate={onNavigate} />
        {/* ☰ trainer menu: Profile, Help, Sound and Log out — kept off the rail so it fits a
            normal screen without scrolling. */}
        <GameTrainerMenu />
        {accountSlot && <div className="game-nav__account">{accountSlot}</div>}
      </div>
    </nav>

    <GameMobileBar
      activePage={activePage}
      onNavigate={onNavigate}
      actingAddress={actingAddress}
      accountSlot={accountSlot}
    />
    </>
  );
}
